import { useState } from 'react';
import { Composer } from './composer/Composer';
import { shipPack } from './packs/ships';
import { avatarPack } from './packs/avatar';
import { monstersPack } from './packs/monsters';
import { webAdapters } from './adapters/web';
import { decodeShare } from './composer/share';
import { ErrorBoundary } from './composer/ui/ErrorBoundary';

const PACKS = {
  ships:    { pack: shipPack,    label: 'Ship',    icon: '🚀' },
  avatar:   { pack: avatarPack,  label: 'Avatar',  icon: '👤' },
  monsters: { pack: monstersPack, label: 'Monster', icon: '👹' },
} as const;

type PackId = keyof typeof PACKS;

function getInitialPackId(): PackId {
  try {
    const match = webAdapters.navigation.readHash().match(/[#&]s=([^&]*)/);
    if (match) {
      const payload = decodeShare(match[1]);
      if (payload && payload.packId in PACKS) return payload.packId as PackId;
    }
  } catch { /* ignore */ }
  return 'ships';
}

export function App() {
  const [packId, setPackId] = useState<PackId>(() => getInitialPackId());
  const { pack } = PACKS[packId];

  // Emoji-only for inactive packs; active pack shows emoji + label so the user
  // sees what they're on. Keeps the switcher under ~150px on a 390px viewport.
  const switcher = (
    <div style={{ display: 'flex', gap: 2, background: 'rgba(0,0,0,0.4)', borderRadius: 6, padding: '2px 3px' }}>
      {(Object.entries(PACKS) as [PackId, typeof PACKS[PackId]][]).map(([id, { label, icon }]) => {
        const active = packId === id;
        return (
          <button
            key={id}
            onClick={() => setPackId(id)}
            aria-label={label}
            title={label}
            style={{
              fontSize: 11,
              fontWeight: 600,
              padding: '2px 7px',
              borderRadius: 4,
              border: 'none',
              cursor: 'pointer',
              background: active ? '#f59e0b' : 'transparent',
              color: active ? '#000' : '#94a3b8',
            }}
          >
            {icon}{active ? ` ${label}` : ''}
          </button>
        );
      })}
    </div>
  );

  return (
    <div style={{ width: '100%', height: '100dvh' }}>
      <ErrorBoundary key={packId}>
        <Composer pack={pack} adapters={webAdapters} packSwitcher={switcher} />
      </ErrorBoundary>
    </div>
  );
}
